import { ApolloLink } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { createConsumer } from '@rails/actioncable';
import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink';
import { from } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import fetchTokenStrategy from 'src/utils/fetch-token-strategy';

const buildWebSocketUrl = async (): Promise<string> => {
  const baseUrl = import.meta.env.VITE_BACKEND_URL?.replace(/^http/, 'ws') || '';
  const wsUrl = new URL(`${baseUrl}/cable`);

  const token = await fetchTokenStrategy();
  if (token) {
    wsUrl.searchParams.set('token', token);
  }

  const impersonatedUserID = localStorage.getItem('impersonatedUserID');
  if (impersonatedUserID) {
    wsUrl.searchParams.set('impersonated_user_id', impersonatedUserID);
  }

  return wsUrl.toString();
};

let cableInstance: ReturnType<typeof createConsumer> | null = null;
const getCable = async () => {
  if (!cableInstance) {
    const cableUrl = await buildWebSocketUrl();
    cableInstance = createConsumer(cableUrl);
  }
  return cableInstance;
};

let actionCableLinkInstance: ActionCableLink | null = null;
const getActionCableLink = async () => {
  if (!actionCableLinkInstance) {
    const cable = await getCable();
    actionCableLinkInstance = new ActionCableLink({ cable });
  }
  return actionCableLinkInstance;
};

export const actionCableLink = new ApolloLink((operation, forward) => {
  if (actionCableLinkInstance) {
    return actionCableLinkInstance.request(operation, forward);
  }
  return from(getActionCableLink()).pipe(mergeMap((link) => link.request(operation, forward)));
});

export const hasSubscriptionOperation = (operation: ApolloLink.Operation) => {
  const definition = getMainDefinition(operation.query);
  return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
};
